/**
 * Thao tác quản trị được phép theo trạng thái người dùng (ADR-0006 mục 4, 5).
 *
 * user-service là chốt chặn thật: sai trạng thái thì trả `409`. Giao diện chỉ
 * dùng bảng này để **ẩn** mục không làm được (ADR-UI-0005) và để máy chủ giả
 * từ chối giống backend.
 *
 * | Trạng thái | Gửi lại lời mời | Khoá | Mở khoá |
 * | ---------- | --------------- | ---- | ------- |
 * | INVITED    | Có              | Có   |         |
 * | ACTIVE     |                 | Có   |         |
 * | DISABLED   |                 |      | Có      |
 *
 * Khoá chính mình thì backend từ chối (`409`) dù trạng thái cho phép: view ẩn
 * mục đó ở dòng của người đang đăng nhập.
 */
import type { UserProfile, UserStatus } from '../types.ts';

export const guiLaiLoiMoiDuoc = (s: UserStatus): boolean => s === 'INVITED';

export const khoaDuoc = (s: UserStatus): boolean => s === 'INVITED' || s === 'ACTIVE';

export const moKhoaDuoc = (s: UserStatus): boolean => s === 'DISABLED';

/**
 * Có mời gán vai trò lớp 2 cho người này không.
 *
 * - Quản trị tổ chức (`TENANT_ADMIN`) có toàn quyền trong tổ chức, không bị kiểm
 *   theo vai trò lớp 2 (ADR-0077): gán thêm vai trò không đổi được gì, nên ẩn.
 * - Không tự gán cho mình: ADR-0004 cấm tự đổi vai trò
 *   (`403 SELF_ROLE_CHANGE_FORBIDDEN`); ngoại lệ "chỉ gỡ bớt" không làm ở giao diện.
 */
export const ganVaiTroDuoc = (
  nguoi: Pick<UserProfile, 'user_id' | 'platform_role'>,
  toiLa: string | undefined,
): boolean => nguoi.user_id !== toiLa && nguoi.platform_role !== 'TENANT_ADMIN';

/** Số lần gửi lại lời mời tối đa mỗi ngày cho một người (ADR-0006 mục 5). */
export const GUI_LAI_TOI_DA_MOI_NGAY = 5;

/** Trần độ dài theo spec `UserInvite`. */
export const DAI_TOI_DA = { email: 320, display_name: 200 } as const;

export type LoiEmail = 'trong' | 'dinhDang' | 'dai';

/**
 * Kiểm email phía giao diện — chỉ bắt lỗi gõ nhầm rõ ràng. Domain có thuộc
 * tenant hay không là việc của server (`EMAIL_DOMAIN_NOT_IN_TENANT`).
 */
export function kiemEmail(email: string): LoiEmail | null {
  const e = email.trim();
  if (!e) return 'trong';
  if (e.length > DAI_TOI_DA.email) return 'dai';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) return 'dinhDang';
  return null;
}

export type LoiTen = 'trong' | 'dai';

/** Tên hiển thị: spec `pattern: ^\S(.*\S)?$` — giao diện gửi bản đã cắt khoảng trắng hai đầu. */
export function kiemTen(ten: string): LoiTen | null {
  const t = ten.trim();
  if (!t) return 'trong';
  if (t.length > DAI_TOI_DA.display_name) return 'dai';
  return null;
}

/*
 * ─────────────── Email nhận thư (ADR-0101) ───────────────
 *
 * Tên đăng nhập (`email`) và email nhận thư (`email_nhan_thu`) tách riêng;
 * mặc định trùng nhau. Thư mời, thư đặt lại mật khẩu và thông báo đi tới email
 * nhận thư.
 */

type HoSoEmail = Pick<UserProfile, 'email' | 'email_nhan_thu'>;

/** Nơi thư của người này gửi tới: email nhận thư, thiếu thì tên đăng nhập. */
export const noiNhanThu = (u: HoSoEmail): string | null => u.email_nhan_thu ?? u.email;

/** Email nhận thư khi **khác** tên đăng nhập (không phân biệt hoa thường); trùng hay thiếu thì `null`. */
export function emailNhanThuRieng(u: HoSoEmail): string | null {
  const nhan = u.email_nhan_thu?.trim();
  if (!nhan || !u.email) return null;
  return nhan.toLowerCase() === u.email.trim().toLowerCase() ? null : nhan;
}

/**
 * Đổi email nhận thư: chỉ quản trị, không cho chính mình (backend trả `403`,
 * ADR-0101). Bản ghi mồ côi (`email` null) không còn tài khoản để đổi.
 */
export const doiEmailNhanThuDuoc = (
  u: Pick<UserProfile, 'user_id' | 'email'>,
  toiLa: string | undefined,
): boolean => u.user_id !== toiLa && u.email !== null;

/**
 * Ô email nhận thư tuỳ chọn của hộp mời: trống là hợp lệ (trùng tên đăng
 * nhập), có chữ thì kiểm như email.
 */
export function kiemEmailTuyChon(email: string): LoiEmail | null {
  return email.trim() ? kiemEmail(email) : null;
}

/*
 * ─────────────── Lấy lại tài khoản ───────────────
 *
 * Ba thao tác giúp một người vào lại được tài khoản. Cùng nguyên tắc với bảng
 * trên: user-service là chốt chặn, giao diện chỉ dùng các hàm này để **ẩn**
 * mục không làm được (ADR-UI-0005).
 */

/** Phần hồ sơ các luật dưới đây cần đọc. */
type HoSoLayLai = Pick<UserProfile, 'user_id' | 'status' | 'email' | 'mfa_configured' | 'login_locked'>;

/**
 * Gửi email đặt lại mật khẩu: chỉ người ACTIVE — người INVITED chưa có mật
 * khẩu để đặt lại (dùng "Gửi lại lời mời"), người DISABLED bị backend từ chối
 * (`409 USER_NOT_ACTIVE`, ADR-0053). Backend cho gửi cho chính mình, nhưng
 * giao diện ẩn: tự đổi ở Tài khoản của tôi thì không phải chờ email. Bản ghi mồ côi
 * (`email` null) không có hộp thư để gửi.
 */
export const guiDatLaiMatKhauDuoc = (u: HoSoLayLai, toiLa: string | undefined): boolean =>
  u.status === 'ACTIVE' && u.user_id !== toiLa && u.email !== null;

/**
 * Đang bị khoá tạm vì nhập sai mật khẩu nhiều lần — nhãn ở cột trạng thái và
 * mục "Mở khoá đăng nhập". Backend gỡ được cho mọi trạng thái kể cả chính
 * mình (ADR-0053); giao diện bỏ người DISABLED: quản trị đã khoá hẳn tài
 * khoản, khoá tạm chẳng thêm gì; mở khoá tài khoản xong mà khoá tạm còn thì
 * nhãn và mục mới hiện ra.
 */
export const dangTamKhoaDangNhap = (u: HoSoLayLai): boolean =>
  u.login_locked === true && u.status !== 'DISABLED';

/**
 * Đặt lại xác thực hai lớp: chỉ khi người đó **đã** cài ứng dụng xác thực
 * (`null` là không rõ, không mời làm). Không cho chính mình: backend trả
 * `403 PERMISSION_DENIED`, tự gỡ ở Tài khoản của tôi.
 */
export const datLaiMfaDuoc = (u: HoSoLayLai, toiLa: string | undefined): boolean =>
  u.mfa_configured === true && u.user_id !== toiLa;

/** Ba lỗi của `password-reset` mà giao diện nói cách xử lý riêng. */
export type LoiDatLaiMatKhau = 'xungDot' | 'quaNhieu' | 'mayChuEmail';

/**
 * Phân loại lỗi gửi email đặt lại mật khẩu theo HTTP status của hợp đồng:
 * `409 USER_NOT_ACTIVE` (chưa nhận lời mời hay đã bị khoá), `429 RATE_LIMITED`
 * (quá 5 lần trong ngày), `503 INTERNAL` (SMTP lỗi hoặc chưa cấu hình — mã là
 * `INTERNAL` nên phải phân theo status, không theo mã). Lỗi khác trả `null`.
 */
export function phanLoaiLoiDatLai(status: number | undefined): LoiDatLaiMatKhau | null {
  if (status === 409) return 'xungDot';
  if (status === 429) return 'quaNhieu';
  if (status === 503) return 'mayChuEmail';
  return null;
}
